import { ref, computed } from 'vue'
import * as Sentry from '@sentry/vue'
import { authService } from '@/services/authService'

// Token storage: sessionStorage, not localStorage. This is a deliberate
// tradeoff for an internal, dev-team-only admin tool -- sessionStorage is
// cleared when the tab closes rather than persisting indefinitely like
// localStorage, while still surviving a page refresh within the same tab
// (unlike keeping tokens in memory only, which would force a re-login on
// every reload). If ERS admin usage grows beyond a handful of internal devs,
// or the security bar needs to be higher, revisit this in favor of an
// httpOnly cookie proxied through ers-api instead.
const ACCESS_TOKEN_KEY = 'ers_admin_access_token'
const REFRESH_TOKEN_KEY = 'ers_admin_refresh_token'

interface JwtPayload {
    email?: string
    name?: string
    role?: string
    exp?: number
    [key: string]: unknown
}

function decodeJwt(token: string): JwtPayload | null {
    try {
        const payload = token.split('.')[1]
        return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as JwtPayload
    } catch {
        return null
    }
}

function isExpired(payload: JwtPayload | null): boolean {
    if (!payload?.exp) return true
    // 30s buffer so a token doesn't expire mid-request.
    return payload.exp * 1000 < Date.now() + 30_000
}

const accessToken = ref<string | null>(sessionStorage.getItem(ACCESS_TOKEN_KEY))
const user = computed<JwtPayload | null>(() =>
    accessToken.value ? decodeJwt(accessToken.value) : null
)

function storeTokens(access: string, refresh?: string): void {
    accessToken.value = access
    sessionStorage.setItem(ACCESS_TOKEN_KEY, access)
    if (refresh) sessionStorage.setItem(REFRESH_TOKEN_KEY, refresh)
}

function clearTokens(): void {
    accessToken.value = null
    sessionStorage.removeItem(ACCESS_TOKEN_KEY)
    sessionStorage.removeItem(REFRESH_TOKEN_KEY)
}

export function useAuth() {
    const isAuthenticated = computed(() =>
        accessToken.value !== null && !isExpired(decodeJwt(accessToken.value))
    )

    /** Redirects the browser to Azure AD login. Does not return -- full page navigation. */
    async function login(): Promise<void> {
        const url = await authService.getLoginUrl()
        window.location.href = url
    }

    /** Called by the /admin/callback route with the `code` query param from the Azure AD redirect. */
    async function handleCallback(code: string): Promise<void> {
        const tokens = await authService.exchangeCode(code)
        storeTokens(tokens.access_token, tokens.refresh_token)
    }

    function logout(): void {
        clearTokens()
    }

    return { user, isAuthenticated, login, handleCallback, logout }
}

/**
 * Returns a valid access token for attaching to API requests, silently
 * refreshing it first if it's expired (or about to be) and a refresh token
 * is available. Returns null if there's no way to get a valid token --
 * callers should treat that as "not logged in."
 */
export async function getValidAccessToken(): Promise<string | null> {
    if (accessToken.value && !isExpired(decodeJwt(accessToken.value))) {
        return accessToken.value
    }

    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
        clearTokens()
        return null
    }

    try {
        const { access_token } = await authService.refresh(refreshToken)
        storeTokens(access_token)
        return access_token
    } catch (error) {
        Sentry.captureException(error)
        clearTokens()
        return null
    }
}

// Plain async helper for the router guard (runs outside Vue component context),
// kept under the same name as the old Firebase-based version for parity.
export async function getCurrentUser(): Promise<JwtPayload | null> {
    const token = await getValidAccessToken()
    return token ? decodeJwt(token) : null
}